import { useMutation, useQueryClient } from "react-query";

import axios from "axios";
import { GEO_MAP_SERVICE_ADDRESS } from "constants/app.constants";

import { GEO_MAP_LAYERS_QUERY_KEYS } from "components/geo-map/hooks/getGeoMapLayers";

import { MAP_LAYER_GROUPS_QUERY_KEYS } from "./useMapLayerGroupsQuery";
import type { MapLayerAccess } from "./useMapLayerGroupsQuery";

// API function to update map layer group access
const updateMapLayerGroupAccess = async (
  group: string,
  userList: string[]
): Promise<void> => {
  const payload = {
    group,
    userList
  };

  const response = await axios.post(
    `${GEO_MAP_SERVICE_ADDRESS}/map-layer-group-access`,
    payload
  );

  if (response.status !== 200) {
    throw new Error(`Failed to update group access: ${response.statusText}`);
  }
};

export const useUpdateMapLayerGroupAccess = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { group: string; userList: string[] },
    { previousData?: MapLayerAccess[] }
  >({
    mutationFn: ({ group, userList }) => updateMapLayerGroupAccess(group, userList),

    onMutate: async ({ group, userList }) => {
      await queryClient.cancelQueries([MAP_LAYER_GROUPS_QUERY_KEYS.mapLayerGroups]);
      const previousData = queryClient.getQueryData<MapLayerAccess[]>([
        MAP_LAYER_GROUPS_QUERY_KEYS.mapLayerGroups
      ]);
      if (previousData) {
        queryClient.setQueryData(
          [MAP_LAYER_GROUPS_QUERY_KEYS.mapLayerGroups],
          previousData.map((item) =>
            item.group === group ? { ...item, userList } : item
          )
        );
      }
      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [MAP_LAYER_GROUPS_QUERY_KEYS.mapLayerGroups],
          context.previousData
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries([MAP_LAYER_GROUPS_QUERY_KEYS.mapLayerGroups]);
      queryClient.invalidateQueries([GEO_MAP_LAYERS_QUERY_KEYS.geoMapLayers]);
    }
  });
};
